import React, { useEffect, useState } from "react";
import { usePayment } from "../hooks";
import { Alert, Box, Grid, MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import TextInput, { fieldSx } from "./inputs/TextInput";
import { Controller, useFormContext } from "react-hook-form";
import { CustomerWallet } from "../models/Service";
import useMaskedInput from "../hooks/useMaskedInput";
import {
  DEFAULT_PHONE_COUNTRY,
  isCompleteNationalNumber,
  phoneCountry,
} from "../constants/phoneCountries";
import { cleanPhoneInput, displayPhone } from "../utils/displayMask";
import { dialCodeAdornment } from "./inputs/adornments";

const PhoneNumberInputs = ({
  isVertical = false,
  service,
  numberError = "",
  revealed = false,
  usesSavedWallets,
}: {
  isVertical?: boolean;
  service: any;
  // The number-against-method refusal, raised by the template at Continue.
  numberError?: string;
  // Continue was pressed: an empty number or an unpicked wallet says so.
  revealed?: boolean;
  // Set by a journey that knows its shape: saved wallets exist only for a
  // found customer. Legacy callers leave it unset and keep the service flag.
  usesSavedWallets?: boolean;
}) => {
  const { t } = useTranslation();
  const {
    phoneNumber,
    setPhoneNumber,
    selectedMethod,
    customerUser,
    setSelectedWallet,
  } = usePayment();
  const customerWallet = customerUser?.customer_wallet_collection || [];

  const { control, watch } = useFormContext();

  const [phoneError, setPhoneError] = useState("");
  const wallet = watch("selectedWallet");

  useEffect(() => {
    if (wallet !== undefined) {
      setSelectedWallet(
        customerWallet.find(
          item => item.wallet_identifier === wallet
        ) as CustomerWallet
      );
    }
  }, [wallet]);

  const validatePhoneNumber = (value: string) => {
    if (!value)
      return t(
        "payment.phone.errors.required",
        "Le numéro de téléphone est requis"
      );
    // The length comes from the country registry, never a fixed nine.
    if (!isCompleteNationalNumber(value, DEFAULT_PHONE_COUNTRY))
      return t("payment.phone.errors.format", {
        length: phoneCountry(DEFAULT_PHONE_COUNTRY)?.nationalLength ?? 0,
      });
    return "";
  };

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = cleanPhoneInput(e.target.value, DEFAULT_PHONE_COUNTRY);
    setPhoneNumber(value);
    setPhoneError(validatePhoneNumber(value));
  };

  // The deposit rail offers no country choice, so it masks for the default.
  const maskedPhone = useMaskedInput({
    value: phoneNumber,
    clean: raw => cleanPhoneInput(raw, DEFAULT_PHONE_COUNTRY),
    display: national => displayPhone(national, DEFAULT_PHONE_COUNTRY),
    onValueChange: value => {
      setPhoneNumber(value);
      setPhoneError(validatePhoneNumber(value));
    },
  });

  const supportsWallets =
    usesSavedWallets ?? !!service?.is_supporting_customer_wallets;

  // Nothing to pick from, so nothing to pay with: said before a method is
  // chosen, because choosing one cannot change it.
  if (supportsWallets && customerWallet.length === 0) {
    return (
      <Alert severity="info" role="status" sx={{ mb: 2 }}>
        {t("payment.paymentForSearch.no_wallet")}
      </Alert>
    );
  }

  if (!selectedMethod) return null;

  const walletError =
    numberError || (revealed && !wallet ? t("errors.field_required") : "");
  const typedError =
    phoneError || numberError || (revealed ? validatePhoneNumber(phoneNumber) : "");

  const itemSizes = isVertical ? { xs: 12 } : { xs: 12, md: 6 };
  return (
    <Box mb={4}>
      <Grid
        container
        spacing={3}
        direction={isVertical ? "column" : "row"}
        alignItems={isVertical ? "stretch" : "flex-start"}
      >
        <Grid item {...itemSizes}>
          {supportsWallets ? (
            <Controller
              name="selectedWallet"
              control={control}
              rules={{ required: t("errors.field_required") }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  select
                  fullWidth
                  sx={fieldSx}
                  label={t("payment.paymentForSearch.select_wallet_label")}
                  error={!!walletError}
                  helperText={
                    walletError || t("payment.paymentForSearch.wallet_hint")
                  }
                  onChange={e => {
                    field.onChange(e);
                    handlePhoneChange(e);
                  }}
                >
                  {/* The preferred number first, then the rest as saved. */}
                  {[...customerWallet]
                    .sort(
                      (a, b) =>
                        (b.is_preferred ? 1 : 0) - (a.is_preferred ? 1 : 0)
                    )
                    .map(wallet => (
                      <MenuItem key={wallet.id} value={wallet.wallet_identifier}>
                        {displayPhone(
                          wallet.wallet_identifier,
                          DEFAULT_PHONE_COUNTRY
                        )}
                        {wallet.is_preferred
                          ? ` (${t("payment.paymentForSearch.preferred")})`
                          : ""}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            />
          ) : (
            <>
              <TextInput
                fullWidth
                id="phoneNumber"
                label={t(
                  "payment.phone.label",
                  "Votre numéro de mobile money *"
                )}
                type="tel"
                inputMode="numeric"
                inputRef={maskedPhone.inputRef}
                value={maskedPhone.value}
                onChange={maskedPhone.onChange}
                error={!!typedError}
                helperText={typedError}
                placeholder={t("payment.phone.placeholder")}
                slotProps={dialCodeAdornment(DEFAULT_PHONE_COUNTRY)}
                variant="outlined"
              />
            </>
          )}
        </Grid>

      </Grid>
    </Box>
  );
};

export default PhoneNumberInputs;
